import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { DarkMode, LightMode, Search } from '@mui/icons-material'
import {
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material'
import { LogOut, MessageCircleQuestion, Settings } from 'lucide-react'

import { useAuth } from '../../contexts/AuthContext'
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext'
import { useTenant } from '../../contexts/TenantContext'
import { useTheme } from '../../contexts/ThemeContext'
import type { Tenant } from '../../types/tenant'
import GlobalFilterDropdown from '../GlobalFilterDropdown'
import HelpDropdown from '../messaging/HelpDropdown'
import NotificationBell from '../messaging/NotificationBell'
import TenantSelector from '../TenantSelector'
import TimeRangeFilterControl from '../TimeRangeFilterControl'

import { onAiAccountEnvFilterPath } from './aiGlobalFilterPaths'

// @purpose Top app header with tenant selector, global filters, search, theme toggle, notifications, help, and user menu
const Header = (): React.JSX.Element => {
  const { isDark, toggleTheme } = useTheme()
  const { logout, isLoginDisabled, user } = useAuth()
  const { isPhaseFuture, hasGlobalTimeFilter } = useFeatureFlags()
  const { selectedTenant, setSelectedTenant, availableTenants } = useTenant()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchValue, setSearchValue] = useState('')
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
  const [helpMenuAnchor, setHelpMenuAnchor] = useState<null | HTMLElement>(null)

  // Check if user is super_admin
  const isSuperAdmin = user?.groups?.some(
    group => group.metadata.role === 'super_admin',
  )

  // Only show tenant selector if user has multiple tenants OR is super_admin
  const showTenantSelector = availableTenants.length > 1 || isSuperAdmin

  // Lock (but still show) the tenant switcher on cross-tenant admin pages. Org
  // Management lists organizations across every tenant and acts on each via its
  // own tenantSlug, so switching the globally-selected tenant there is
  // ambiguous and misleading. Match the route exactly, its trailing-slash
  // variant, and any descendant routes — but not sibling paths that merely share
  // the prefix (e.g. /admin/org-management-foo).
  const orgManagementPath = '/admin/org-management'
  const isTenantSwitchDisabled =
    location.pathname === orgManagementPath ||
    location.pathname.startsWith(`${orgManagementPath}/`)

  // Determine if global filter should be disabled based on current route
  // Enable only on /, /risk, /inventory (infrastructure), /security (detection), and the
  // in-scope AI Security pages (identities, catalog index, findings,
  // security-concerns, and incidents index — see aiGlobalFilterPaths.ts).
  const isGlobalFilterDisabled =
    location.pathname !== '/' &&
    !location.pathname.startsWith('/risk') &&
    !location.pathname.startsWith('/inventory') &&
    !location.pathname.startsWith('/security') &&
    !onAiAccountEnvFilterPath(location.pathname)

  // The Time Range control is enabled on the same routes as the account/env
  // dropdown PLUS the AI Applications catalog INDEX, which supports time
  // filtering (date_from/date_to/timestamp_type) but NOT account/env filtering
  // — so the two controls are gated separately here rather than sharing one
  // predicate. The detail route (/ai-applications/catalog/:id) does not consume
  // the filter, so match the index path exactly (tolerating a trailing slash)
  // rather than by prefix.
  const isAiApplicationsCatalogIndex =
    location.pathname === '/ai-applications/catalog' ||
    location.pathname === '/ai-applications/catalog/'
  // All Security Concerns sub-tab routes (/types, /detections, /signals) get the
  // time filter, so match by prefix. (Detections ignores it — it's a static
  // catalog — but the control is page-level, matching the catalog-detail precedent.)
  const isAiSecurityConcernsRoute = location.pathname.startsWith(
    '/ai-applications/security-concerns',
  )
  // AI Incidents is a single index route — match it exactly (tolerating a
  // trailing slash) rather than by prefix.
  const isAiIncidentsRoute =
    location.pathname === '/ai-applications/incidents' ||
    location.pathname === '/ai-applications/incidents/'
  const isTimeFilterDisabled =
    isGlobalFilterDisabled &&
    !isAiApplicationsCatalogIndex &&
    !isAiSecurityConcernsRoute &&
    !isAiIncidentsRoute

  const handleTenantChange = (tenant: Tenant) => {
    setSelectedTenant(tenant)
    // Tenant change will automatically persist via TenantContext
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
    // In a real app, you'd trigger search functionality
  }

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
  }

  const getDisplayName = () => {
    if (!user) return ''
    // Use given_name and family_name if available, otherwise email
    if (user.given_name && user.family_name) {
      return `${user.given_name} ${user.family_name}`
    }
    return user.email || ''
  }

  const getDisplayEmail = () => {
    if (!user) return ''
    // Show email if we have name fields, otherwise don't duplicate
    if (user.given_name && user.family_name) {
      return user.email
    }
    return ''
  }

  const getInitialsName = () => {
    if (!user) return ''
    // Use given_name and family_name if available, otherwise email
    if (user.given_name && user.family_name) {
      return `${user.given_name} ${user.family_name}`
    }
    return user.email || ''
  }

  const handleSettingsClick = () => {
    navigate('/settings')
  }

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget)
  }

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null)
  }

  const handleLogout = () => {
    handleUserMenuClose()
    logout()
  }

  const _handleProfile = () => {
    handleUserMenuClose()
    // Navigate to profile page or open profile modal
    console.log('Profile clicked')
  }

  const handleHelpMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHelpMenuAnchor(event.currentTarget)
  }

  const handleHelpMenuClose = () => {
    setHelpMenuAnchor(null)
  }

  return (
    <header className="relative bg-header dark:bg-header-dark bg-gradient-to-b from-header-from to-header-to dark:from-header-dark-from dark:to-header-dark-to px-6 py-1 h-[60px]">
      {/* Brand accent hairline under the banner — soft Seafoam in light,
          Atomic in dark. One uniform 1px line (spans the sidebar logo cell
          too) so the band reads continuous, not a hard divider. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-header-accent/50 dark:bg-header-accent/40"
      />
      <div className="flex items-center justify-between">
        {/* Left Side - Tenant Selector and Global Filters */}
        <div className="flex items-center gap-4">
          {showTenantSelector && (
            <TenantSelector
              tenants={availableTenants}
              selectedTenant={selectedTenant}
              onTenantChange={handleTenantChange}
              disabled={isTenantSwitchDisabled}
            />
          )}
          <GlobalFilterDropdown disabled={isGlobalFilterDisabled} />
          {hasGlobalTimeFilter() && (
            <TimeRangeFilterControl disabled={isTimeFilterDisabled} />
          )}
        </div>

        {/* Center - Global Search */}
        {isPhaseFuture() && (
          <div className="flex-1 mx-8">
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search assets, incidents, vulnerabilities..."
              value={searchValue}
              onChange={handleSearchChange}
              className="bg-white/10 header-search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="icon-size-20" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        )}

        {/* Right Side - Actions & User */}
        <div className="flex items-center space-x-2">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 text-header-icon hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Toggle Theme"
          >
            {isDark ? (
              <LightMode sx={{ fontSize: 24 }} />
            ) : (
              <DarkMode sx={{ fontSize: 24 }} />
            )}
          </button>

          {/* Notifications */}
          <NotificationBell />

          {/* User Settings */}
          <button
            onClick={handleSettingsClick}
            className="p-2 text-header-icon hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Open Settings"
          >
            <Settings className="w-6 h-6" />
          </button>

          {/* Help/Support */}
          <button
            onClick={handleHelpMenuOpen}
            className="p-2 text-header-icon hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Help & Support"
            data-testid="header-button-help"
          >
            <MessageCircleQuestion className="w-6 h-6" />
          </button>

          {/* Help Dropdown */}
          <HelpDropdown
            anchorEl={helpMenuAnchor}
            open={Boolean(helpMenuAnchor)}
            onClose={handleHelpMenuClose}
          />

          {/* User Profile */}
          {user && getDisplayName() && (
            <button
              onClick={handleUserMenuOpen}
              className="flex items-center space-x-3 pl-4 pr-2 py-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              {/* ⚠ Gradient uses brand-green-700 → blue-600 (both ≥4.5:1 on white,
                  WCAG AA) for the white initials; keep in sync with UserMonogram /
                  UserAvatarRenderer. */}
              <div className="w-8 h-8 bg-gradient-to-r from-brand-green-700 to-blue-600 text-white rounded-full flex items-center justify-center font-medium text-sm">
                {getUserInitials(getInitialsName())}
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-white">
                  {getDisplayName()}
                </div>
                {getDisplayEmail() && (
                  <div className="text-xs text-header-muted">
                    {getDisplayEmail()}
                  </div>
                )}
              </div>
            </button>
          )}

          {/* User Menu */}
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            onClick={handleUserMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {/* <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem> */}

            {!isLoginDisabled && (
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogOut size={16} />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </div>
      </div>
    </header>
  )
}

export default Header
